/**
 * Un trabajo, abierto. Es la pantalla donde el técnico pasa el rato en casa del cliente.
 *
 * ── El orden de la pantalla no es casual ──
 *
 * Arriba va lo que se necesita ANTES de bajarse del carro: qué hay que hacer, de quién es,
 * dónde queda y cómo llegar. En el medio, lo que se hace durante: empezar, escribir qué se
 * hizo, las fotos. Abajo del todo, lo que se hace al final: la firma y darlo por terminado.
 *
 * Es el orden en que ocurre el trabajo, no el orden en que están los campos en la base.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { COLOR, PRIORIDAD, ESTADO, TIPO, s } from './estilos';
import { achicar, enKb } from './foto';
import Firma from './Firma';

/**
 * El enlace para abrir la dirección en el mapa del celular.
 *
 * Si el cliente tiene coordenadas se usan esas, porque en los barrios donde trabajamos la
 * dirección escrita muchas veces no la encuentra ningún mapa («Vereda El Palmar, casa de
 * tabla verde»). Si no las tiene, se busca por texto y al menos deja al técnico cerca.
 *
 * Se usa `geo:` y no un enlace de Google Maps: `geo:` abre la app de mapas que el técnico
 * tenga puesta —Google Maps, Waze, la que sea— en vez de obligarlo a una.
 */
function enlaceDelMapa(t) {
  if (t.cliente_latitud && t.cliente_longitud) {
    const etiqueta = encodeURIComponent(t.cliente_nombre || 'Cliente');
    return `geo:${t.cliente_latitud},${t.cliente_longitud}?q=${t.cliente_latitud},${t.cliente_longitud}(${etiqueta})`;
  }
  if (t.cliente_direccion) {
    /* Santander de Quilichao va agregado porque «Calle 5 #20-77» a secas existe en media
       Colombia y el mapa puede mandar al técnico a otro departamento. */
    return `geo:0,0?q=${encodeURIComponent(t.cliente_direccion + ', Santander de Quilichao, Cauca')}`;
  }
  return null;
}

const cuando = (fecha) => {
  if (!fecha) return null;
  const d = new Date(fecha);
  const hoy = new Date();
  const mismoDia = d.toDateString() === hoy.toDateString();
  const hora = d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
  if (mismoDia) return `Hoy a las ${hora}`;
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' }) + ' · ' + hora;
};

export default function FichaTicket({ ticketId, hayRed, deLaLista, onVolver, onCambio }) {
  const [t, setT] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [soloLoGuardado, setSoloLoGuardado] = useState(false);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState('');
  const [solucion, setSolucion] = useState('');
  const [comentario, setComentario] = useState('');
  const [mirando, setMirando] = useState(null);

  const traer = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const { data } = await api.get(`/api/tickets/${ticketId}`);
      setT(data);
      setSoloLoGuardado(false);
      setSolucion(data.solucion || '');
    } catch (e) {
      /**
       * Sin señal se arma la ficha con lo que ya venía en la lista del día.
       *
       * Sin esto, el técnico ve su trabajo en la lista, lo toca para mirar la dirección, y
       * se encuentra un «no se pudo abrir». Justo el momento para el que se guardó el día.
       * Lo que trae la lista es lo que hace falta para llegar: qué hay que hacer, de quién
       * es, el teléfono y la dirección. Lo que NO trae —la historia, las fotos, la firma—
       * la pantalla no lo enseña en vez de enseñarlo vacío, que sería decir que no hay.
       *
       * Si ni siquiera está en la lista (se abrió por otro lado), ahí sí se avisa.
       */
      if (deLaLista) {
        setT({ ...deLaLista, seguimiento: null, fotos: null });
        setSoloLoGuardado(true);
        setSolucion(deLaLista.solucion || '');
      } else {
        setError(e.response?.data?.error || 'No se pudo abrir el trabajo. Revise la señal.');
      }
    } finally { setCargando(false); }
  }, [ticketId, deLaLista]);

  useEffect(() => { traer(); }, [traer]);

  /** Todo lo que escribe pasa por aquí, para que el aviso de error sea uno solo y de verdad. */
  const guardar = async (que, hacer) => {
    if (!hayRed) return;
    setGuardando(que); setError('');
    try {
      await hacer();
      await traer();
      if (onCambio) onCambio();
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo guardar. Revise la señal e intente otra vez.');
    } finally { setGuardando(''); }
  };

  /**
   * Cambiar el estado se lleva SIEMPRE lo que haya escrito el técnico.
   *
   * Cazado probando la app el 29-sep-2026: «Marcar terminado» exigía haber escrito qué se
   * hizo —y no dejaba pasar sin texto— pero después mandaba solo el estado. El texto se
   * perdía. El técnico escribía, pulsaba, veía «Terminado», y en la oficina aparecía
   * «se marcó como resuelto sin escribir qué se hizo».
   *
   * Es el peor tipo de fallo que hay aquí: la pantalla dice que se guardó y no se guardó, y
   * el que pierde el trabajo es el que sí lo hizo bien.
   */
  const cambiarEstado = (estado) =>
    guardar('estado', () => api.put(`/api/tickets/${ticketId}`, { estado, solucion: solucion || undefined }));

  const guardarSolucion = () =>
    guardar('solucion', () => api.put(`/api/tickets/${ticketId}`, { solucion }));

  const comentar = () =>
    guardar('comentario', async () => {
      await api.post(`/api/tickets/${ticketId}/seguimiento`, { comentario });
      setComentario('');
    });

  const tomarFoto = (e) => {
    const archivo = e.target.files?.[0];
    /* El input se limpia siempre, hasta si falla: sin esto, volver a escoger LA MISMA foto
       no dispara nada porque el valor no cambió, y parece que la app se trabó. */
    e.target.value = '';
    if (!archivo) return;
    guardar('foto', async () => {
      const { base64, mime } = await achicar(archivo);
      await api.post(`/api/tickets/${ticketId}/fotos`, {
        foto_base64: base64, mime_type: mime, nombre: archivo.name,
      });
    });
  };

  const borrarFoto = (fotoId) => {
    if (!window.confirm('¿Borrar esta foto?')) return;
    guardar('foto', () => api.delete(`/api/tickets/${ticketId}/fotos/${fotoId}`));
  };

  const verFoto = async (foto) => {
    setMirando({ cargando: true, nombre: foto.nombre });
    try {
      const { data } = await api.get(`/api/tickets/${ticketId}/fotos/${foto.id}`);
      setMirando({ src: `data:${data.mime_type};base64,${data.foto_base64}`, nombre: data.nombre, id: foto.id });
    } catch (err) {
      setMirando(null);
      setError('No se pudo abrir la foto. Revise la señal.');
    }
  };

  if (cargando) return <div style={s.vacio}>Abriendo el trabajo…</div>;
  if (!t) {
    return (
      <div style={s.vacio}>
        <div style={{ fontSize: '40px', marginBottom: '10px' }}>😕</div>
        {error || 'No se pudo abrir este trabajo.'}
        <div style={{ marginTop: '18px' }}>
          <button style={s.botonSuave} onClick={onVolver}>← Volver</button>
        </div>
      </div>
    );
  }

  const eCfg = ESTADO[t.estado] || ESTADO.abierto;
  const pCfg = PRIORIDAD[t.prioridad] || PRIORIDAD.normal;
  const mapa = enlaceDelMapa(t);
  const terminado = ['resuelto', 'cerrado', 'cancelado'].includes(t.estado);
  const ocupado = !!guardando;

  return (
    <div style={{ ...s.cuerpo, gap: '14px' }}>

      {!hayRed && (
        <div style={{ ...s.aviso, background: 'rgba(245,158,11,0.12)', color: '#FCD34D' }}>
          <span>📵</span>
          <span>Sin señal: puede leer el trabajo, pero no guardar nada todavía.</span>
        </div>
      )}
      {error && (
        <div style={{ ...s.aviso, background: 'rgba(239,68,68,0.12)', color: '#FCA5A5' }}>
          <span>⚠️</span><span style={{ overflowWrap: 'anywhere' }}>{error}</span>
        </div>
      )}

      {/* ── Qué hay que hacer ── */}
      <div style={s.tarjeta}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
          <span style={{ ...s.chip, background: `${eCfg.color}22`, color: eCfg.color }}>
            {eCfg.icono} {eCfg.label}
          </span>
          <span style={{ ...s.chip, background: `${pCfg.color}22`, color: pCfg.color }}>{pCfg.label}</span>
          <span style={{ ...s.chip, background: 'rgba(255,255,255,0.06)', color: COLOR.suave }}>
            {TIPO[t.tipo] || t.tipo}
          </span>
        </div>
        <div style={s.titulo}>{t.titulo}</div>
        <div style={{ ...s.sub, marginTop: '4px' }}>N.º {t.numero}</div>
        {t.descripcion && (
          <div style={{ ...s.sub, marginTop: '10px', color: COLOR.texto, lineHeight: 1.55 }}>
            {t.descripcion}
          </div>
        )}
        {t.fecha_programada && (
          <div style={{ ...s.sub, marginTop: '10px' }}>🗓️ {cuando(t.fecha_programada)}</div>
        )}
        {/* De quién es. Va aquí arriba y no abajo porque es lo que decide si uno sale o no:
            con todos viendo todo, ir a un trabajo que ya cogió otro es el viaje perdido. */}
        <div style={{ ...s.sub, marginTop: '10px' }}>
          {t.tecnico_nombre ? `🔧 Lo tiene ${t.tecnico_nombre}` : '✋ Sin asignar — puede tomarlo'}
        </div>
        {t.resuelto_por_nombre && (
          <div style={{ ...s.sub, marginTop: '4px', color: COLOR.verde }}>
            ✅ Lo resolvió {t.resuelto_por_nombre}
          </div>
        )}
      </div>

      {/* ── De quién es y dónde queda ── */}
      {t.cliente_nombre && (
        <div style={s.tarjeta}>
          <div style={{ ...s.titulo, fontSize: '15px' }}>{t.cliente_nombre}</div>
          {t.cliente_telefono && (
            <div style={{ ...s.sub, marginTop: '4px' }}>📞 {t.cliente_telefono}</div>
          )}
          {t.cliente_direccion && (
            <div style={{ ...s.sub, marginTop: '4px' }}>📍 {t.cliente_direccion}</div>
          )}
          {mapa && (
            <a href={mapa} style={{ textDecoration: 'none', display: 'block', marginTop: '12px' }}>
              <span style={{ ...s.boton, background: 'rgba(0,229,255,0.12)', color: COLOR.acento }}>
                🗺️ Cómo llegar
              </span>
            </a>
          )}
        </div>
      )}

      {/* ── Empezar / terminar ── */}
      {!terminado && (
        <div style={{ display: 'grid', gap: '10px' }}>
          {/* Empezar un trabajo libre es TOMARLO: el servidor pone su nombre en el ticket y
              los demás lo ven ocupado. Por eso el botón lo dice, en vez de dejar que el
              técnico se entere después de que el trabajo quedó a su nombre. */}
          {t.estado === 'abierto' && (
            <button
              style={{ ...s.boton, background: COLOR.morado, color: '#fff', opacity: hayRed ? 1 : 0.5 }}
              disabled={!hayRed || ocupado}
              onClick={() => cambiarEstado('en_proceso')}
            >
              {guardando === 'estado' ? 'Guardando…'
                : t.tecnico_id ? '▶ Empezar este trabajo' : '✋ Tomarlo y empezar'}
            </button>
          )}
          <button
            style={{ ...s.boton, background: COLOR.verde, color: '#052e21', opacity: hayRed ? 1 : 0.5 }}
            disabled={!hayRed || ocupado}
            onClick={() => {
              if (!solucion.trim()) {
                setError('Escriba primero qué hizo — es lo que queda como historia del trabajo.');
                return;
              }
              cambiarEstado('resuelto');
            }}
          >
            {guardando === 'estado' ? 'Guardando…' : '✅ Marcar terminado'}
          </button>
        </div>
      )}

      {/* ── Qué hice ── */}
      <div style={s.tarjeta}>
        <div style={{ ...s.titulo, fontSize: '14px', marginBottom: '8px' }}>Qué hice</div>
        <textarea
          style={{ ...s.campo, minHeight: '110px', resize: 'vertical' }}
          placeholder="Qué encontró y qué hizo. Esto es lo que va a leer la oficina."
          value={solucion}
          onChange={(e) => setSolucion(e.target.value)}
          disabled={!hayRed}
        />
        <button
          style={{ ...s.botonSuave, marginTop: '10px', opacity: hayRed ? 1 : 0.5 }}
          disabled={!hayRed || ocupado || solucion === (t.solucion || '')}
          onClick={guardarSolucion}
        >
          {guardando === 'solucion' ? 'Guardando…' : '💾 Guardar'}
        </button>
      </div>

      {/* Las fotos, la firma y la historia solo se enseñan con señal: guardados están la
          lista del día y nada más, y una sección vacía diría «no hay» en vez de «no se
          pudo mirar», que no es lo mismo. */}
      {soloLoGuardado && (
        <div style={{ ...s.aviso, background: 'rgba(255,255,255,0.05)', color: COLOR.suave }}>
          <span>📵</span>
          <span>Las fotos, la firma y la historia se ven cuando vuelva la señal.</span>
        </div>
      )}

      {/* ── Fotos ── */}
      {!soloLoGuardado && <div style={s.tarjeta}>
        <div style={{ ...s.titulo, fontSize: '14px', marginBottom: '10px' }}>
          Fotos del trabajo {t.fotos?.length ? `(${t.fotos.length})` : ''}
        </div>
        {!!t.fotos?.length && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))',
            gap: '8px', marginBottom: '12px',
          }}>
            {t.fotos.map((f) => (
              <button
                key={f.id}
                onClick={() => verFoto(f)}
                style={{
                  aspectRatio: '1', borderRadius: '10px', minWidth: 0,
                  border: `1px solid ${COLOR.borde}`, background: 'rgba(255,255,255,0.04)',
                  color: COLOR.suave, fontSize: '11px', cursor: 'pointer', padding: '6px',
                  display: 'grid', placeItems: 'center', gap: '2px',
                }}
              >
                <span style={{ fontSize: '22px' }}>🖼️</span>
                <span>Ver</span>
              </button>
            ))}
          </div>
        )}
        {/* El `capture` abre la cámara directo en vez del carrete: el técnico va a tomar la
            foto ahí mismo, no a buscarla. */}
        <label style={{ ...s.botonSuave, opacity: hayRed ? 1 : 0.5, cursor: hayRed ? 'pointer' : 'not-allowed' }}>
          {guardando === 'foto' ? 'Subiendo…' : '📷 Tomar una foto'}
          <input
            type="file" accept="image/*" capture="environment"
            style={{ display: 'none' }} onChange={tomarFoto} disabled={!hayRed || ocupado}
          />
        </label>
        <div style={{ ...s.sub, fontSize: '12px', marginTop: '8px' }}>
          Se achican solas antes de subir, para que no se gasten los datos.
        </div>
      </div>}

      {/* ── Firma ── */}
      {!soloLoGuardado && <div style={s.tarjeta}>
        <div style={{ ...s.titulo, fontSize: '14px', marginBottom: '10px' }}>Firma del cliente</div>
        {t.tiene_firma ? (
          <div style={{ ...s.aviso, background: 'rgba(16,185,129,0.1)', color: '#6EE7B7', padding: '12px 0' }}>
            <span>✔</span>
            <span>
              Firmado por <strong>{t.firma_nombre}</strong>
              {t.firma_en && <><br /><span style={{ color: COLOR.suave }}>{cuando(t.firma_en)}</span></>}
            </span>
          </div>
        ) : !hayRed ? (
          <div style={s.sub}>Necesita señal para guardar la firma.</div>
        ) : (
          <Firma
            guardando={guardando === 'firma'}
            onGuardar={(datos) => guardar('firma', () => api.post(`/api/tickets/${ticketId}/firma`, datos))}
          />
        )}
      </div>}

      {/* ── Historia ── */}
      {!soloLoGuardado && <div style={s.tarjeta}>
        <div style={{ ...s.titulo, fontSize: '14px', marginBottom: '10px' }}>Historia</div>
        {!t.seguimiento?.length && <div style={s.sub}>Todavía no hay nada anotado.</div>}
        <div style={{ display: 'grid', gap: '10px' }}>
          {t.seguimiento?.map((c) => (
            <div key={c.id} style={{ borderLeft: `2px solid ${COLOR.borde}`, paddingLeft: '10px', minWidth: 0 }}>
              <div style={{ fontSize: '13px', lineHeight: 1.5, overflowWrap: 'anywhere' }}>{c.comentario}</div>
              <div style={{ ...s.sub, fontSize: '11px', marginTop: '2px' }}>
                {c.usuario_nombre} · {cuando(c.creado_en)}
              </div>
            </div>
          ))}
        </div>
        <textarea
          style={{ ...s.campo, minHeight: '70px', marginTop: '12px', resize: 'vertical' }}
          placeholder="Anotar algo…"
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          disabled={!hayRed}
        />
        <button
          style={{ ...s.botonSuave, marginTop: '8px', opacity: hayRed && comentario.trim() ? 1 : 0.5 }}
          disabled={!hayRed || ocupado || !comentario.trim()}
          onClick={comentar}
        >
          {guardando === 'comentario' ? 'Guardando…' : '➕ Anotar'}
        </button>
      </div>}

      {/* ── La foto en grande ── */}
      {mirando && (
        <div
          onClick={() => setMirando(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.92)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '16px', gap: '14px',
          }}
        >
          {mirando.cargando
            ? <div style={{ color: COLOR.suave }}>Abriendo la foto…</div>
            : <img src={mirando.src} alt={mirando.nombre || 'Foto del trabajo'}
                   style={{ maxWidth: '100%', maxHeight: '72vh', borderRadius: '12px' }} />}
          <div style={{ display: 'grid', gap: '10px', width: '100%', maxWidth: '320px' }} onClick={(e) => e.stopPropagation()}>
            {!mirando.cargando && hayRed && !terminado && (
              <button
                style={{ ...s.botonSuave, borderColor: 'rgba(239,68,68,0.4)', color: '#FCA5A5' }}
                onClick={() => { const id = mirando.id; setMirando(null); borrarFoto(id); }}
              >🗑️ Borrar esta foto</button>
            )}
            <button style={s.botonSuave} onClick={() => setMirando(null)}>Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
}

export { enlaceDelMapa, enKb };
